import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { ClipboardContentType, ClipboardItem } from "@/types";

interface ClipboardItemRow {
  id: string;
  content: string;
  content_type: ClipboardContentType;
  pinned: number;
  created_at: number;
}

function rowToItem(row: ClipboardItemRow): ClipboardItem {
  return {
    id: row.id,
    content: row.content,
    contentType: row.content_type,
    pinned: row.pinned === 1,
    createdAt: row.created_at,
  };
}

/** History depth for unpinned items — pinned items are exempt. Keeps the
 * table (and the always-resident-in-memory address space of a clipboard
 * monitor) bounded without an explicit retention setting. */
const HISTORY_LIMIT = 200;

export const clipboardRepository = {
  async list(): Promise<ClipboardItem[]> {
    const db = await getDb();
    const rows = await db.select<ClipboardItemRow[]>(
      "SELECT * FROM clipboard_items ORDER BY pinned DESC, created_at DESC",
    );
    return rows.map(rowToItem);
  },

  async add(content: string, contentType: ClipboardContentType = "text"): Promise<ClipboardItem> {
    const db = await getDb();
    const item: ClipboardItem = { id: createId("clip"), content, contentType, pinned: false, createdAt: Date.now() };
    await db.execute(
      "INSERT INTO clipboard_items (id, content, content_type, pinned, created_at) VALUES ($1, $2, $3, 0, $4)",
      [item.id, item.content, item.contentType, item.createdAt],
    );
    await db.execute(
      `DELETE FROM clipboard_items WHERE pinned = 0 AND id NOT IN (
         SELECT id FROM clipboard_items WHERE pinned = 0 ORDER BY created_at DESC LIMIT $1
       )`,
      [HISTORY_LIMIT],
    );
    return item;
  },

  async setPinned(id: string, pinned: boolean): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE clipboard_items SET pinned = $1 WHERE id = $2", [pinned ? 1 : 0, id]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM clipboard_items WHERE id = $1", [id]);
  },

  async clear(): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM clipboard_items WHERE pinned = 0");
  },
};
